import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ItemCard, PointLevelSettings } from '../../types/pointItemNote';
import UnsavedCloseConfirm from './UnsavedCloseConfirm';
import { resolveItemCardImageSrc } from '../../utils/itemCardImage';

type Props = {
  open: boolean;
  levelSettings: PointLevelSettings;
  levelupItems: ItemCard[];
  /** +/- 스텝 (없으면 1) */
  pointStep?: number;
  coinStep?: number;
  onClose: () => void;
  onSave: (settings: PointLevelSettings) => void;
};

type TabKey = 'basic' | 'reward';

const TAB_LABELS: Record<TabKey, string> = {
  basic: '기본 설정',
  reward: '레벨별 보상 설정',
};

const LEVEL_COLORS = [
  '#FC5555',
  '#FF9933',
  '#CCCC00',
  '#669900',
  '#119473',
  '#0066CC',
  '#9900CC',
  '#920049',
  '#D60093',
];

function levelBadgeColor(level: number): string {
  if (level % 10 === 0) return 'var(--color-accent-strong, #10504a)';
  return LEVEL_COLORS[(level % 10) - 1] ?? LEVEL_COLORS[0];
}

const CardPlaceholder: React.FC<{ size?: number }> = ({ size = 48 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <rect
      x="2"
      y="6"
      width="12"
      height="16"
      rx="2"
      fill="#f0f4f3"
      stroke="#555"
      strokeWidth="1.5"
      transform="rotate(-10 8 14)"
    />
    <rect
      x="6"
      y="4"
      width="12"
      height="16"
      rx="2"
      fill="#f8f8f8"
      stroke="#555"
      strokeWidth="1.5"
    />
    <rect
      x="10"
      y="6"
      width="12"
      height="16"
      rx="2"
      fill="#fafafa"
      stroke="#555"
      strokeWidth="1.5"
      transform="rotate(10 16 14)"
    />
  </svg>
);

const LevelSettingsModal: React.FC<Props> = ({
  open,
  levelSettings,
  levelupItems,
  pointStep = 1,
  coinStep = 10,
  onClose,
  onSave,
}) => {
  const [levelUpPoints, setLevelUpPoints] = useState<number | ''>(50);
  const [selectedLevel, setSelectedLevel] = useState(1);
  const [levelCoins, setLevelCoins] = useState<Record<number, number>>({});
  const [levelItemCards, setLevelItemCards] = useState<Record<number, string[]>>({});
  const [isEditingLevel, setIsEditingLevel] = useState(false);
  const [editingLevelValue, setEditingLevelValue] = useState('1');
  const [confirmClose, setConfirmClose] = useState(false);
  const [tab, setTab] = useState<TabKey>('basic');

  useEffect(() => {
    if (!open) return;
    setLevelUpPoints(levelSettings.levelUpPoints || 50);
    setSelectedLevel(1);
    setLevelCoins({ ...levelSettings.levelCoins });
    setLevelItemCards(
      Object.fromEntries(
        Object.entries(levelSettings.levelItemCards).map(([k, v]) => [Number(k), [...v]])
      )
    );
    setIsEditingLevel(false);
    setConfirmClose(false);
    setTab('basic');
  }, [open, levelSettings]);

  const snapshot = useMemo(
    () =>
      JSON.stringify({
        levelUpPoints: levelSettings.levelUpPoints,
        levelCoins: levelSettings.levelCoins,
        levelItemCards: levelSettings.levelItemCards,
      }),
    [levelSettings]
  );

  const currentSnapshot = useMemo(
    () =>
      JSON.stringify({
        levelUpPoints: typeof levelUpPoints === 'number' ? levelUpPoints : 50,
        levelCoins,
        levelItemCards,
      }),
    [levelUpPoints, levelCoins, levelItemCards]
  );

  const dirty = open && snapshot !== currentSnapshot;

  if (!open) return null;

  const step = Math.max(1, pointStep);
  const cStep = Math.max(1, coinStep);
  const unit = typeof levelUpPoints === 'number' ? levelUpPoints : 50;
  const selectedItems = levelItemCards[selectedLevel] ?? [];

  const commitLevelEdit = () => {
    const trimmed = editingLevelValue.trim();
    if (trimmed === '') setSelectedLevel(1);
    else {
      const v = parseInt(trimmed, 10);
      setSelectedLevel(!Number.isNaN(v) && v >= 1 && v <= 100 ? v : 1);
    }
    setIsEditingLevel(false);
  };

  const handleClose = () => {
    if (dirty) setConfirmClose(true);
    else onClose();
  };

  const handleSave = () => {
    const finalUnit = Math.max(1, typeof levelUpPoints === 'number' ? levelUpPoints : 50);
    onSave({
      levelUpPoints: finalUnit,
      levelCoins,
      levelItemCards,
    });
    onClose();
  };

  const toggleItem = (itemCardId: string) => {
    const cur = levelItemCards[selectedLevel] ?? [];
    const next = cur.includes(itemCardId)
      ? cur.filter((id) => id !== itemCardId)
      : [...cur, itemCardId];
    setLevelItemCards({ ...levelItemCards, [selectedLevel]: next });
  };

  const renderThumb = (item: ItemCard) => {
    const src = resolveItemCardImageSrc(item.cardImage);
    if (src) {
      return <img src={src} alt="" />;
    }
    return <CardPlaceholder size={32} />;
  };

  return createPortal(
    <div className="cp-apn-modal-overlay" role="presentation" onClick={handleClose}>
      <div
        className="cp-apn-modal cp-pin-settings-modal cp-pin-level-modal"
        data-tutorial-pin="level-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-pin-level-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cp-apn-modal-header cp-pin-modal-header">
          <h2 id="cp-pin-level-title">레벨 설정</h2>
          <button type="button" className="cp-apn-icon-btn" onClick={handleClose} aria-label="닫기">
            ×
          </button>
        </header>

        <div className="cp-pin-bar-tabs cp-pin-bar-tabs--2" role="tablist" data-tutorial-pin="level-tabs">
          {(Object.keys(TAB_LABELS) as TabKey[]).map((key) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              className={`cp-pin-bar-tab${tab === key ? ' is-active' : ''}`}
              onClick={() => setTab(key)}
            >
              {TAB_LABELS[key]}
            </button>
          ))}
        </div>

        <div className="cp-apn-modal-body cp-pin-modal-body cp-pin-level-modal-body">
          {tab === 'basic' ? (
            <section
              className="cp-pin-level-section cp-pin-level-section--basic"
              data-tutorial-pin="level-basic"
            >
              <div className="cp-pin-level-basic-center">
                <span className="cp-pin-level-basic-label">레벨업 포인트</span>
                <div className="cp-pin-level-basic-control">
                  <button
                    type="button"
                    className="cp-pin-level-basic-nav"
                    aria-label="레벨업 포인트 감소"
                    onClick={() => {
                      const cur = typeof levelUpPoints === 'number' ? levelUpPoints : 50;
                      if (cur > step) setLevelUpPoints(cur - step);
                    }}
                  >
                    &lt;
                  </button>
                  <input
                    className="cp-pin-level-basic-input"
                    type="text"
                    inputMode="numeric"
                    aria-label="레벨업 포인트"
                    value={levelUpPoints}
                    onChange={(e) => {
                      const v = e.target.value.trim();
                      if (v === '') setLevelUpPoints('');
                      else if (/^\d+$/.test(v)) setLevelUpPoints(parseInt(v, 10));
                    }}
                    onBlur={() => {
                      if (levelUpPoints === '' || !levelUpPoints) setLevelUpPoints(50);
                      else if (typeof levelUpPoints === 'number') {
                        setLevelUpPoints(Math.min(1000, Math.max(1, levelUpPoints)));
                      }
                    }}
                    onFocus={(e) => e.target.select()}
                  />
                  <button
                    type="button"
                    className="cp-pin-level-basic-nav"
                    aria-label="레벨업 포인트 증가"
                    onClick={() => {
                      const cur = typeof levelUpPoints === 'number' ? levelUpPoints : 50;
                      setLevelUpPoints(Math.min(1000, cur + step));
                    }}
                  >
                    &gt;
                  </button>
                </div>
                <p className="cp-pin-level-desc cp-pin-level-basic-desc">
                  설정한 포인트 단위로 레벨이 올라갑니다.
                  <br />
                  (예: {unit}점마다 레벨업)
                </p>
              </div>
            </section>
          ) : (
            <section
              className="cp-pin-level-section cp-pin-level-section--reward"
              data-tutorial-pin="level-reward"
            >
              <div className="cp-pin-level-select-row">
                <button
                  type="button"
                  className="cp-pin-level-nav-btn"
                  onClick={() => setSelectedLevel((v) => Math.max(1, v - 1))}
                >
                  &lt;
                </button>
                {isEditingLevel ? (
                  <input
                    className="cp-pin-level-select-input"
                    type="text"
                    inputMode="numeric"
                    value={editingLevelValue}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || /^\d+$/.test(val)) setEditingLevelValue(val);
                    }}
                    onBlur={commitLevelEdit}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitLevelEdit();
                    }}
                    autoFocus
                  />
                ) : (
                  <button
                    type="button"
                    className="cp-pin-level-select-display"
                    style={{ background: levelBadgeColor(selectedLevel) }}
                    onClick={() => {
                      setEditingLevelValue(String(selectedLevel));
                      setIsEditingLevel(true);
                    }}
                  >
                    {selectedLevel}레벨
                  </button>
                )}
                <button
                  type="button"
                  className="cp-pin-level-nav-btn"
                  onClick={() => setSelectedLevel((v) => Math.min(100, v + 1))}
                >
                  &gt;
                </button>
              </div>

              <div
                className="cp-pin-level-reward-box"
                style={{ borderColor: levelBadgeColor(selectedLevel) }}
              >
                <h4 className="cp-pin-level-reward-sub">{selectedLevel}레벨 보상 코인</h4>
                <div className="cp-pin-level-basic-control cp-pin-level-basic-control--sm">
                  <button
                    type="button"
                    className="cp-pin-level-basic-nav"
                    aria-label="보상 코인 감소"
                    onClick={() =>
                      setLevelCoins({
                        ...levelCoins,
                        [selectedLevel]: Math.max(0, (levelCoins[selectedLevel] || 0) - cStep),
                      })
                    }
                  >
                    −
                  </button>
                  <span className="cp-pin-level-coin-display">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <circle cx="12" cy="12" r="10" fill="#f39c12" />
                      <circle cx="12" cy="12" r="8" fill="#f1c40f" />
                      <text x="12" y="16" fontSize="12" fontWeight="bold" fill="#f39c12" textAnchor="middle">C</text>
                    </svg>
                    <input
                      className="cp-pin-level-basic-input"
                      style={{ color: '#f39c12' }}
                      type="text"
                      inputMode="numeric"
                      aria-label="보상 코인"
                      value={levelCoins[selectedLevel] || 0}
                      onChange={(e) => {
                        const v = e.target.value.trim();
                        if (v === '') {
                          setLevelCoins({ ...levelCoins, [selectedLevel]: 0 });
                        } else if (/^\d+$/.test(v)) {
                          setLevelCoins({ ...levelCoins, [selectedLevel]: parseInt(v, 10) });
                        }
                      }}
                      onFocus={(e) => e.target.select()}
                    />
                  </span>
                  <button
                    type="button"
                    className="cp-pin-level-basic-nav"
                    aria-label="보상 코인 증가"
                    onClick={() =>
                      setLevelCoins({
                        ...levelCoins,
                        [selectedLevel]: (levelCoins[selectedLevel] || 0) + cStep,
                      })
                    }
                  >
                    +
                  </button>
                </div>

                <h4 className="cp-pin-level-reward-sub">{selectedLevel}레벨 보상 아이템</h4>
                {levelupItems.length === 0 ? (
                  <p className="cp-pin-level-empty">
                    레벨업 아이템이 없습니다. 아이템 카드 설정에서 먼저 레벨업 아이템을 만들어 주세요.
                  </p>
                ) : (
                  <div className="cp-pin-level-item-grid">
                    {levelupItems.map((item) => {
                      const selected = selectedItems.includes(item.itemCardId);
                      return (
                        <div key={item.itemCardId} className="cp-pin-level-item-slot">
                          <button
                            type="button"
                            className={`cp-pin-store-card cp-pin-level-store-card${selected ? ' is-selected' : ''}`}
                            aria-pressed={selected}
                            onClick={() => toggleItem(item.itemCardId)}
                          >
                            <div className="cp-pin-store-card-icon-wrap">
                              <div className="cp-pin-store-card-icon">{renderThumb(item)}</div>
                              {selected ? (
                                <span className="cp-pin-store-card-check" aria-hidden>
                                  ✓
                                </span>
                              ) : null}
                            </div>
                            <p className="cp-pin-store-card-name">{item.name || '(이름 없음)'}</p>
                            {item.memo?.trim() ? (
                              <p className="cp-pin-store-card-memo">{item.memo}</p>
                            ) : (
                              <p className="cp-pin-store-card-memo">&nbsp;</p>
                            )}
                            <div className="cp-pin-store-card-meta" aria-hidden />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>
          )}
        </div>

        <footer className="cp-pin-modal-footer" data-tutorial-pin="level-footer">
          <button type="button" className="cp-pin-modal-btn cp-pin-modal-btn--secondary" onClick={handleClose}>
            닫기
          </button>
          <button
            type="button"
            className="cp-pin-modal-btn cp-pin-modal-btn--primary"
            data-tutorial-pin="level-save"
            onClick={handleSave}
          >
            저장
          </button>
        </footer>

        {confirmClose ? (
          <UnsavedCloseConfirm
            open
            onCancel={() => setConfirmClose(false)}
            onConfirm={() => {
              setConfirmClose(false);
              onClose();
            }}
          />
        ) : null}
      </div>
    </div>,
    document.body
  );
};

export default LevelSettingsModal;
